import * as signalR from '@microsoft/signalr';

let notificationConnection: signalR.HubConnection | null = null;
const apiUrl = process.env.VITE_API_URL || process.env.API_URL;

// Simple in-memory de-dupe for bursty hub events (same pass approved twice quickly)
const recentEventTimestamps = new Map<string, number>();
function shouldDedup(key: string, windowMs = 3000) {
  const now = Date.now();
  const last = recentEventTimestamps.get(key) ?? 0;
  if (now - last < windowMs) return true;
  recentEventTimestamps.set(key, now);
  return false;
}

export type OrderNotification =
  | { type: 'new'    ; orderId: number }
  | { type: 'cancel' ; orderId: number }
  | { type: 'ready'  ; orderId: number; pickupCode: string; pickupLocation: string };

export async function startNotificationConnection(
  onHallPass?: (hp: any) => void,
  context: 'STUDENT' | 'nonStudent' = 'nonStudent',
  onOrder?: (payload: OrderNotification) => void,
  userId?: string
) {
  const HP_DEBUG = true; // TEMP DIAGNOSTIC FLAG – set false to silence
  if (!notificationConnection) {
    notificationConnection = new signalR.HubConnectionBuilder()
      .withUrl(apiUrl + "/hubs/notification", { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Diagnostic handlers (once)
    notificationConnection.onreconnecting((error) => {
      console.warn("SignalR reconnecting due to error:", error);
    });
    notificationConnection.onreconnected((connectionId) => {
      console.info("SignalR reconnected. New connectionId:", connectionId);
    });
    notificationConnection.onclose((error) => {
      console.error("SignalR connection closed with error:", error);
      setTimeout(() => {
        // ★ keep onOrder and userId after reconnect
        startNotificationConnection(onHallPass, context, onOrder, userId);
      }, 5000);
    });
  } else {
    // Clear any previously registered handlers
    notificationConnection.off("ReceiveHallPass");
    notificationConnection.off("ApprovedHallPass");
    notificationConnection.off("DeniedHallPass");
    notificationConnection.off("SummonsHallPass");
    notificationConnection.off("ReturnHallPass");
    notificationConnection.off("ExpiredHallPass");
    notificationConnection.off("CancelledHallPass");
    notificationConnection.off("OrderPostAcornOrder");   // ★ NEW
    notificationConnection.off("OrderCancel");           // ★ NEW
    notificationConnection.off("OrderMarkReady");        // ← ensure previous handler cleared
  }

  // Register event handlers based on context
  if (context !== "STUDENT") {
    // Helper to validate hall pass payload completeness (teacher context)
    const validateHallPassPayload = (label: string, hp: any) => {
      if (!HP_DEBUG || !hp || typeof hp !== 'object') return;
      // Minimum fields the frontend needs to correctly render without fallbacks
      const requiredFields = [
        'hallPassId',              // unique id
        'hallPassTypeId',          // to resolve description/duration
        'studentId',               // link to student
        'studentName',             // display name
        'hallPassStatusId',        // numeric status enum
        'status',                  // textual status (optional but helpful)
        'requestDecisionedAt',     // canonical start time (or requestAt/createdAt)
        'durationMinutes',         // explicit duration
      ];
      const present = Object.keys(hp);
      const missing = requiredFields.filter(f => hp[f] === undefined || hp[f] === null);
      if (missing.length) {
        console.warn(`[HP][WARN][${label}] Missing expected field(s): ${missing.join(', ')}. Present keys:`, present, 'Raw payload:', hp);
      }
      // Specific check: if both hallPassTypeId and any destination/location fields absent
      const hasType = hp.hallPassTypeId != null;
      const hasLocationName = hp.destinationLocationName || hp.location || hp?.destinationLocation?.locationName || hp?.explicitDestinationLocation?.locationName;
      if (!hasType && !hasLocationName) {
        console.warn(`[HP][WARN][${label}] Cannot derive destination/description because hallPassTypeId and destination location fields are all missing.`);
      }
    };
    notificationConnection.on("ReceiveHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReceiveHallPass]', hp);
      validateHallPassPayload('ReceiveHallPass', hp);

      // Normalize common shape for downstream consumers
      const normalized = (() => {
        const n: any = { ...hp };

        // prefer explicit then type-provided path
        n.explicitPath = n.explicitPath ?? n.ExplicitPath ?? null;
        n.hallPassTypePath = n.hallPassTypePath ?? n.HallPassTypePath ?? null;

        n.startLocation = n.startLocation ?? n.StartLocation ?? n.explicitStartLocation ?? n.ExplicitStartLocation ?? null;
        n.destinationLocation = n.destinationLocation ?? n.DestinationLocation ?? n.explicitDestinationLocation ?? n.ExplicitDestinationLocation ?? null;

        // unify id/time/duration fields
        n.hallPassId = n.hallPassId ?? n.id ?? n.Id;
        n.requestDecisionedAt = n.requestDecisionedAt ?? n.RequestDecisionedAt ?? null;
        n.requestAt = n.requestAt ?? n.RequestAt ?? n.createdAt ?? n.CreatedAt ?? null;
        n.durationMinutes = n.durationMinutes ?? n.DurationMinutes ?? n?.hallPassType?.hallPassTypeDuration ?? null;

        // convenience mirrors for downstream fallbacks
        n.pathId =
          n.pathId ??
          n?.explicitPath?.pathId ??
          n?.hallPassTypePath?.pathId ??
          n?.PathId;

        n.startLocationId =
          n.startLocationId ??
          n?.startLocation?.locationId ??
          n?.StartLocationId;

        n.destinationLocationId =
          n.destinationLocationId ??
          n?.destinationLocation?.locationId ??
          n?.DestinationLocationId;

        n.destinationLocationName =
          n.destinationLocationName ??
          n?.destinationLocation?.name ??
          n?.DestinationLocationName ??
          n?.destinationName;

        return n;
      })();

      // Forward to local subscribers
      onHallPass?.(normalized);

      // Notify app-wide data hook (map/listeners)
      try {
        window.dispatchEvent(
          new CustomEvent('hallPassUpdated', {
            detail: { type: 'upsert', hallPass: normalized },
          })
        );
      } catch (e) {
        console.warn('Failed to dispatch hallPassUpdated event', e);
      }
    });
    notificationConnection.on("CancelledHallPass", (hallPassId) => {
      if (HP_DEBUG) console.log('[HP][RAW][CancelledHallPass]', hallPassId);
      onHallPass?.({ hallPassId, status: "cancelled" });
    });
    notificationConnection.on('SummonsHallPass', (...args: any[]) => {
      let payload: any;
      if (args.length === 1 && typeof args[0] === "object") {
        const h = args[0];
        payload = {
          ...h,
          status: "summons",
          summonedBy: h.summonedBy ?? h.SummonedBy ?? true,
          summonedTo: h.summonedTo ?? h.SummonedTo,
        };
      } else {
        const [hallPassId, message, notes] = args;
        payload = {
          hallPassId: hallPassId?.toString?.() ?? hallPassId,
          status: "summons",
          message,
          notes,
          summonedBy: true,
        };
      }
      if (HP_DEBUG) console.log('[HP][RAW][SummonsHallPass]', payload);
      validateHallPassPayload('SummonsHallPass', payload);
      onHallPass?.(payload);
    });
    notificationConnection.on("ReturnHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReturnHallPass]', hp);
      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ReturnHallPass] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        // API inconsistency: server sent just the hallPassId primitive
        payload = { hallPassId: hp, status: 'returned' };
        if (HP_DEBUG) console.log('[HP][COERCE][ReturnHallPass] primitive -> object', payload);
      } else {
        // Ensure hallPassId field exists & status
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
            if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status || payload.status === 'return') {
          payload.status = 'returned';
        }
      }
      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ReturnHallPass] Missing hallPassId after normalization:', payload);
      }
      onHallPass?.(payload);
    });
    notificationConnection.on("ExpiredHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ExpiredHallPass]', hp);
      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ExpiredHallPass] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        payload = { hallPassId: hp, status: 'expired' };
        if (HP_DEBUG) console.log('[HP][COERCE][ExpiredHallPass] primitive -> object', payload);
      } else {
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
          if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status) payload.status = 'expired';
      }
      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ExpiredHallPass] Missing hallPassId after normalization:', payload);
      }
      onHallPass?.(payload);
    });
  }
   else {
    notificationConnection.on("ApprovedHallPass", (hallPassId, desc) => {
      console.log("ApprovedHallPass", hallPassId, desc);
      const id = hallPassId?.toString?.() ?? hallPassId;
      // Deduplicate against ReceiveHallPass that may also arrive
      if (id && shouldDedup(`approved:${id}`)) return;
      onHallPass?.({ hallPassId: id, status: "approved", desc });
    });
    notificationConnection.on("DeniedHallPass", (hallPassId, desc) => {
      onHallPass?.({ hallPassId, status: "denied", desc });
    });
    notificationConnection.on('SummonsHallPass', (...args: any[]) => {
      let payload: any;
      if (args.length === 1 && typeof args[0] === "object") {
        const h = args[0];
        payload = {
          ...h,
          status: "summons",
          summonedBy: h.summonedBy ?? h.SummonedBy ?? true,
          summonedTo: h.summonedTo ?? h.SummonedTo,
        };
      } else {
        const [hallPassId, message, notes] = args;
        payload = {
          hallPassId: hallPassId?.toString?.() ?? hallPassId,
          status: "summons",
          message,
          notes,
          summonedBy: true,
        };
      }
      if (HP_DEBUG) console.log('[HP][RAW][SummonsHallPass][STUDENT]', payload);
      onHallPass?.(payload);
    });
    // ←–– new: teacher pushes a fresh pass to student
    notificationConnection.on("ReceiveHallPass", (hp: any) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReceiveHallPass][STUDENT]', hp);

      // Filter hall pass notifications to only intended student
      const hallPassStudentId = hp.studentId;

      if (hallPassStudentId && userId) {
        if (hallPassStudentId !== userId) {
          console.log('ReceiveHallPass: notification not for current student, ignoring');
          return;
        }
      } else if (!userId) {
        console.log('ReceiveHallPass: no user ID available, ignoring hall pass notification');
        return;
      }
      // Allow through if hallPassStudentId is undefined for backward compatibility

      const id = hp?.hallPassId?.toString?.() || hp?.id?.toString?.();
      if (id && shouldDedup(`approved:${id}`)) return; // coalesce with ApprovedHallPass

      onHallPass?.({ ...hp, status: "approved", hallPassId: id ?? hp?.hallPassId });
    });

    // Forward any hall pass update as a "HallPassUpdated" event for the student.
    notificationConnection.on("ReturnHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReturnHallPass][STUDENT]', hp);

      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ReturnHallPass][STUDENT] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        // API inconsistency: server sent just the hallPassId primitive
        payload = { hallPassId: hp, status: 'returned' };
        if (HP_DEBUG) console.log('[HP][COERCE][ReturnHallPass][STUDENT] primitive -> object', payload);
      } else {
        // Ensure hallPassId field exists & status
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
            if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status || payload.status === 'return') {
          payload.status = 'returned';
        }
      }

      // Filter by student ID for return notifications
      const hallPassStudentId = payload?.studentId;

      if (hallPassStudentId && userId) {
        if (hallPassStudentId !== userId) {
          console.log('ReturnHallPass: notification not for current student, ignoring');
          return;
        }
      } else if (!userId) {
        console.log('ReturnHallPass: no user ID available, ignoring hall pass notification');
        return;
      }

      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ReturnHallPass][STUDENT] Missing hallPassId after normalization:', payload);
      }

      onHallPass?.(payload);
    });

    notificationConnection.on("ExpiredHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ExpiredHallPass][STUDENT]', hp);

      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ExpiredHallPass][STUDENT] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        payload = { hallPassId: hp, status: 'expired' };
        if (HP_DEBUG) console.log('[HP][COERCE][ExpiredHallPass][STUDENT] primitive -> object', payload);
      } else {
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
          if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status) payload.status = 'expired';
      }

      // Filter by student ID for expired notifications
      const hallPassStudentId = payload?.studentId;

      if (hallPassStudentId && userId) {
        if (hallPassStudentId !== userId) {
          console.log('ExpiredHallPass: notification not for current student, ignoring');
          return;
        }
      } else if (!userId) {
        console.log('ExpiredHallPass: no user ID available, ignoring hall pass notification');
        return;
      }

      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ExpiredHallPass][STUDENT] Missing hallPassId after normalization:', payload);
      }

      onHallPass?.(payload);
    });
  }

  // ★ NEW ➜ order notifications (same for both contexts)
  notificationConnection.on("OrderPostAcornOrder", (orderId: number) => {
    onOrder?.({ type: 'new',    orderId });
  });
  notificationConnection.on("OrderCancel", (orderId: number) => {
    onOrder?.({ type: 'cancel', orderId });
  });
  notificationConnection.off("OrderMarkReady");
  notificationConnection.on(
    "OrderMarkReady",
    (orderId: number, pickupCode: string, pickupLocation: string) => {
      onOrder?.({ type: 'ready', orderId, pickupCode, pickupLocation });
    }
  );

  if (notificationConnection.state === signalR.HubConnectionState.Disconnected) {
    try {
      await notificationConnection.start();
      console.info("SignalR connection started.");
    } catch (err) {
      console.error("Error starting notification connection:", err);
    }
  }

  // NEW ➜ expose the connection to the caller
  return notificationConnection;
}
